if (!customElements.get('product-slider-thumbnails')) {
  /**
   *  @class
   *  @function ProductSliderThumbnails
   */
  class ProductSliderThumbnails extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('change', this.setupZoomListeners);
    }
    connectedCallback() {
      this.product_container = this.closest('.thb-product-detail');
      this.thumbnail_container = this.product_container.querySelector('.product-thumbnail-container');
      this.video_containers = this.querySelectorAll('.product-single__media-external-video--play');

      this.setOptions();
      // A touch screen swipes the photos in the browser's own scrolling; a mouse keeps Flickity.
      // Flickity moves a slide one step per animation frame, and in Low Power Mode Safari runs
      // half the frames, so the photos crawled (merchant, 4 Oct 2026, iPhone 17 Pro Max). The
      // browser's scrolling runs by the clock: the swipe follows the finger, carries its momentum,
      // and keeps its pace in Low Power Mode, as stock Reformation's phone gallery does. A native
      // row ends at the last photo instead of wrapping to the first.
      this.touch = window.matchMedia('(hover: none) and (pointer: coarse)');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.start();
      this.touch.addEventListener('change', () => {
        this.stop();
        this.setOptions();
        this.start();
      });
    }
    start() {
      if (this.touch.matches) {
        this.initNative();
      } else {
        this.init();
      }
    }
    stop() {
      if (this.eventController) this.eventController.abort();
      if (this.flkty) {
        this.flkty.destroy();
        this.flkty = null;
      }
      this.native = false;
      this.classList.remove('is-native');
    }
    // The photos the gallery shows: with "Hide unselected variants' media" on, only the chosen
    // variant's are marked active, as Flickity's cellSelector reads them.
    cells() {
      return Array.from(this.querySelectorAll('.product-images__slide.is-active'));
    }
    slideLeft(slide) {
      return slide.getBoundingClientRect().left - this.getBoundingClientRect().left + this.scrollLeft;
    }
    initNative() {
      this.native = true;
      this.classList.add('is-native');
      this.eventController = new AbortController();
      const signal = this.eventController.signal;
      const first = this.querySelector('.product-images__slide.is-active.is-initial-selected') || this.cells()[0];
      if (first) {
        // 'instant': product.css gives .product-images smooth scrolling, and the page opens on its photo.
        this.scrollTo({ left: this.slideLeft(first), behavior: 'instant' });
        this.markSelected(first, 'auto');
      }
      // The photo on show is read once the row comes to rest, from wherever the finger left it.
      this.addEventListener('scroll', () => {
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.settleNative(), 120);
      }, { passive: true, signal });
      this.addEventListener('scrollend', () => this.settleNative(), { signal });
      this.video_containers.forEach((container) => {
        container.querySelector('button').addEventListener('click', () => {
          container.setAttribute('hidden', '');
        }, { signal });
      });
      Array.from(this.thumbnails).filter((element) => element.clientWidth > 0).forEach((thumbnail) => {
        thumbnail.addEventListener('click', () => this.thumbnailClick(thumbnail), { signal });
      });
      this.setupZoomListeners();
    }
    settleNative() {
      clearTimeout(this.settleTimer);
      const cells = this.cells();
      if (!cells.length) return;
      let nearest = cells[0];
      cells.forEach((cell) => {
        if (Math.abs(this.slideLeft(cell) - this.scrollLeft) < Math.abs(this.slideLeft(nearest) - this.scrollLeft)) nearest = cell;
      });
      if (nearest !== this.selectedSlide) this.markSelected(nearest);
    }
    // The photo on show: its slide is .is-selected (the zoom view opens from it), its thumbnail
    // wears the ring and scrolls into the strip, and a video on the photo left behind pauses.
    markSelected(slide, behavior = 'smooth') {
      const previous = this.selectedSlide;
      this.selectedSlide = slide;
      this.cells().forEach((cell) => cell.classList.toggle('is-selected', cell === slide));
      if (previous && previous !== slide) {
        const media = previous.querySelector('.product-single__media');
        if (media) this.pauseMedia(media);
      }
      const thumb = this.thumbnail_container.querySelector('#' + slide.id.replace('Slide-', 'Thumb-'));
      this.thumbnails.forEach((item) => {
        item.classList.toggle('is-initial-selected', item === thumb);
        item.setAttribute('aria-current', String(item === thumb));
      });
      if (thumb) this.scrollToThumbnail(thumb, behavior);
    }
    // Go to a photo by its index among those shown (the zoom view's arrows).
    selectIndex(index) {
      if (this.native) {
        const cell = this.cells()[index];
        if (cell) {
          this.scrollTo({ left: this.slideLeft(cell), behavior: 'instant' });
          this.markSelected(cell, 'auto');
        }
      } else if (this.flkty) {
        this.flkty.select(index);
      }
    }
    setOptions() {
      this.thumbnails = this.thumbnail_container.querySelectorAll('.product-thumbnail');
      this.prev_button = this.querySelector('.flickity-prev');
      this.next_button = this.querySelector('.flickity-next');
      this.options = {
        wrapAround: true,
        pageDots: false,
        contain: true,
        adaptiveHeight: true,
        initialIndex: '.is-initial-selected',
        prevNextButtons: false,
        fade: false,
        cellSelector: '.product-images__slide.is-active'
      };
    }
    init() {
      this.flkty = new Flickity(this, this.options);
      this.selectedIndex = this.flkty.selectedIndex;
      this.setupEvents();
      this.setupZoomListeners();
      // Flickity fires no change event for the initial cell, so the active
      // thumbnail would otherwise start out of view on a long list. Position
      // it instantly rather than animating a scroll on page load.
      this.scrollToActiveThumbnail('auto');
    }
    reInit() {
      // A native row shows the active photos by CSS; the selectCell that follows moves to the one chosen.
      if (this.native) return;
      if (this.eventController) {
        this.eventController.abort();
      }

      this.flkty.destroy();
      this.setOptions();
      this.flkty = new Flickity(this, this.options);
      this.setupEvents();
      this.selectedIndex = this.flkty.selectedIndex;
      // A variant change marks the new cell as initial before rebuilding, so
      // the follow-up selectCell lands on it without firing change — scroll
      // the matching thumbnail into view explicitly.
      this.scrollToActiveThumbnail();
    }
    setupEvents() {
      if (this.eventController) {
        this.eventController.abort();
      }
      this.eventController = new AbortController();
      const signal = this.eventController.signal;

      if (this.prev_button) {
        this.prev_button.addEventListener('click', () => {
          this.flkty.previous();
        }, { signal });
        this.prev_button.addEventListener('keyup', (event) => {
          this.flkty.previous();
          event.preventDefault();
        }, { signal });
      }
      if (this.next_button) {
        this.next_button.addEventListener('click', () => {
          this.flkty.next();
        }, { signal });
        this.next_button.addEventListener('keyup', (event) => {
          this.flkty.next();
          event.preventDefault();
        }, { signal });
      }
      this.video_containers.forEach((container) => {
        container.querySelector('button').addEventListener('click', function () {
          container.setAttribute('hidden', '');
        });
      });
      this.flkty.on('settle', (index) => {
        this.selectedIndex = index;
      });
      this.flkty.on('change', (index) => {
        let previous_slide = this.flkty.cells[this.selectedIndex].element,
          previous_media = previous_slide.querySelector('.product-single__media'),
          current_slide = this.flkty.cells[index].element,
          thumbId = current_slide.id.replace('Slide-', 'Thumb-'),
          active_thumb = this.thumbnail_container.querySelector('#' + thumbId);

        this.thumbnails.forEach((item) => {
          item.classList.remove('is-initial-selected');
          item.setAttribute('aria-current', 'false');
        });
        if (active_thumb) {
          active_thumb.classList.add('is-initial-selected');
          active_thumb.setAttribute('aria-current', 'true');
          this.scrollToThumbnail(active_thumb);
        }
        this.pauseMedia(previous_media);
      });

      setTimeout(() => {
        let active_thumbs = Array.from(this.thumbnails).filter(element => element.clientWidth > 0);
        active_thumbs.forEach((thumbnail) => {
          thumbnail.addEventListener('click', () => {
            this.thumbnailClick(thumbnail);
          }, { signal });
        });
      });
    }
    scrollToActiveThumbnail(behavior = 'smooth') {
      if (!this.flkty || !this.flkty.cells.length) {
        return;
      }
      const current_slide = this.flkty.cells[this.flkty.selectedIndex].element,
        thumbId = current_slide.id.replace('Slide-', 'Thumb-'),
        active_thumb = this.thumbnail_container.querySelector('#' + thumbId);
      if (active_thumb) {
        this.scrollToThumbnail(active_thumb, behavior);
      }
    }
    scrollToThumbnail(thumb, behavior = 'smooth') {
      requestAnimationFrame(() => {
        if (thumb.offsetParent === null) {
          return;
        }
        const windowHalfHeight = thumb.offsetParent.clientHeight / 2,
          windowHalfWidth = thumb.offsetParent.clientWidth / 2;
        thumb.parentElement.scrollTo({
          left: thumb.offsetLeft - windowHalfWidth + thumb.clientWidth / 2,
          top: thumb.offsetTop - windowHalfHeight + thumb.clientHeight / 2,
          behavior: behavior
        });
      });
    }
    pauseMedia(media) {
      if (media.classList.contains('product-single__media-external-video')) {
        if (media.dataset.provider === 'youtube') {
          media.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
            event: "command",
            func: "pauseVideo",
            args: ""
          }), "*");
        } else if (media.dataset.provider === 'vimeo') {
          media.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
            method: "pause"
          }), "*");
        }
        media.querySelector('.product-single__media-external-video--play').removeAttribute('hidden');
      } else if (media.classList.contains('product-single__media-native-video')) {
        media.querySelector('video').pause();
      }
    }
    thumbnailClick(thumbnail) {
      this.thumbnails.forEach((el) => {
        el.classList.remove('is-initial-selected');
        el.setAttribute('aria-current', 'false');
      });
      thumbnail.classList.add('is-initial-selected');
      thumbnail.setAttribute('aria-current', 'true');
      let mediaId = '#' + thumbnail.id.replace('Thumb-', 'Slide-');
      this.selectCell(mediaId);
    }
    // A 3D model takes the finger while it is turned (product-model.js), so the row stops swiping.
    setDraggable(draggable) {
      if (this.native) {
        this.style.overflowX = draggable ? '' : 'hidden';
        return;
      }
      this.flkty.options.draggable = draggable;
      this.flkty.updateDraggable();
    }
    selectCell(mediaId) {
      if (this.native) {
        const slide = this.querySelector(mediaId);
        if (!slide) return;
        this.scrollTo({ left: this.slideLeft(slide), behavior: this.reduceMotion.matches ? 'instant' : 'smooth' });
        this.markSelected(slide);
        return;
      }
      this.flkty.selectCell(mediaId);
    }
    setupZoomListeners() {
      if (!this.querySelectorAll('.product-single__media-zoom').length) {
        return;
      }
      this.setEventListeners();

      document.addEventListener('product:variant-change', (e) => {
        if (e.detail && e.detail.variantTitle && e.detail.variantTitle !== 'Default Title') {
          this.dataset.variantTitle = e.detail.variantTitle;
          this.updatePswpProductInfo();
        }
      });
    }
    buildItems() {
      this.activeImages = Array.from(this.querySelectorAll('.product-images__slide.is-active .product-single__media-image'));

      return this.activeImages.map((item, index) => {
        let activelink = item.querySelector('.product-single__media-zoom');

        activelink.dataset.index = index;
        return {
          src: activelink.getAttribute('href'),
          msrc: activelink.dataset.msrc,
          thumbSrc: activelink.dataset.thumbSrc || activelink.dataset.msrc,
          w: activelink.dataset.w,
          h: activelink.dataset.h
        };
      });
    }
    setEventListeners() {
      this.links = this.querySelectorAll('.product-single__media-zoom');
      this.pswpElement = document.querySelectorAll('.pswp')[0];
      this.pswpOptions = {
        maxSpreadZoom: 2,
        loop: true,
        allowPanToNext: true,
        closeOnScroll: false,
        showHideOpacity: false,
        arrowKeys: true,
        history: false,
        captionEl: false,
        fullscreenEl: false,
        zoomEl: false,
        shareEl: false,
        counterEl: true,
        arrowEl: true,
        preloaderEl: true,
        getThumbBoundsFn: () => {
          const thumbnail = this.querySelector('.product-images__slide.is-selected'),
            pageYScroll = window.pageYOffset || document.documentElement.scrollTop,
            rect = thumbnail.getBoundingClientRect();
          return {
            x: rect.left,
            y: rect.top + pageYScroll,
            w: rect.width
          };
        }
      };

      this.links.forEach((link => {
        link.addEventListener('click', (e) => this.zoomClick(e, link));
      }));
    }
    buildPswpThumbnails(items) {
      const container = this.pswpElement.querySelector('.pswp__thumbs-inner');
      if (!container) return;
      container.innerHTML = '';
      if (this.dataset.lightboxThumbnails === 'false' || items.length <= 1) {
        container.parentElement.style.display = 'none';
        return;
      }
      container.parentElement.style.display = '';
      items.forEach((item, index) => {
        const thumb = document.createElement('div');
        thumb.className = 'pswp__thumb';
        thumb.dataset.index = index;
        const img = document.createElement('img');
        img.src = item.thumbSrc;
        img.alt = '';
        img.loading = 'lazy';
        thumb.appendChild(img);
        container.appendChild(thumb);
      });
    }
    updatePswpProductInfo() {
      const infoEl = this.pswpElement.querySelector('.pswp__product-info');
      if (!infoEl) return;
      if (this.dataset.lightboxProductInfo === 'false') {
        infoEl.style.display = 'none';
        return;
      }
      infoEl.style.display = '';
      const titleEl = infoEl.querySelector('.pswp__product-title');
      const variantEl = infoEl.querySelector('.pswp__variant-title');
      if (titleEl) titleEl.textContent = this.dataset.productTitle || '';
      if (variantEl) variantEl.textContent = this.dataset.variantTitle || '';
    }
    syncPswpThumbnails(index) {
      const container = this.pswpElement.querySelector('.pswp__thumbs-inner');
      if (!container) return;
      const thumbs = container.querySelectorAll('.pswp__thumb');
      thumbs.forEach((t, i) => {
        t.classList.toggle('is-active', i === index);
      });
      const activeThumb = container.querySelector('.pswp__thumb.is-active');
      if (activeThumb) {
        activeThumb.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
      }
    }
    zoomClick(e, link) {
      this.items = this.buildItems();
      this.pswpOptions.index = parseInt(link.dataset.index, 10);
      if (typeof PhotoSwipe !== 'undefined') {
        this.buildPswpThumbnails(this.items);
        this.updatePswpProductInfo();

        const bottomBar = this.pswpElement.querySelector('.pswp__bottom-bar');
        if (bottomBar) {
          ['mousedown', 'touchstart', 'pointerdown'].forEach(evtName => {
            bottomBar.addEventListener(evtName, (evt) => evt.stopPropagation());
          });
        }

        let pswp = new PhotoSwipe(this.pswpElement, PhotoSwipeUI_Default, this.items, this.pswpOptions);
        const _this = this;

        pswp.listen('firstUpdate', function () {
          pswp.listen('parseVerticalMargin', function (item) {
            item.vGap = {
              top: 50,
              bottom: 140
            };
          });
        });

        const scrollWrap = this.pswpElement.querySelector('.pswp__scroll-wrap');
        const resetScroll = function() {
          if (scrollWrap) scrollWrap.scrollLeft = 0;
          _this.pswpElement.scrollLeft = 0;
        };

        pswp.listen('beforeChange', function () {
          const index = pswp.getCurrentIndex();
          _this.syncPswpThumbnails(index);
          resetScroll();
          _this.selectIndex(index);
        });

        const thumbsInner = this.pswpElement.querySelector('.pswp__thumbs-inner');
        if (thumbsInner) {
          thumbsInner.addEventListener('click', function handler(evt) {
            evt.stopPropagation();
            const thumb = evt.target.closest('.pswp__thumb');
            if (thumb) {
              pswp.goTo(parseInt(thumb.dataset.index, 10));
              resetScroll();
            }
          });
        }

        pswp.listen('close', function () {
          const container = _this.pswpElement.querySelector('.pswp__thumbs-inner');
          if (container) container.innerHTML = '';
        });

        pswp.init();
        this.syncPswpThumbnails(this.pswpOptions.index);
      }
      e.preventDefault();
    }
  }
  customElements.define('product-slider-thumbnails', ProductSliderThumbnails);
}
